/**
 * Thin delta over DVD. The root ThemeProvider supplies colors, spacing,
 * typography, and mq to every styled-component on this surface; this file
 * holds only what DVD does not — the dark-canvas palette, the two display
 * sizes, and the reading columns the bands share. Model:
 * src/dvd/blog/tokens/index.ts.
 *
 * Values DVD already carries are deliberately absent, so the surface reaches
 * for the shared scale instead of forking it: the accent ramp's outer stops
 * are `blue300` and `pink400`, and the page canvas is `blue800`.
 *
 * Plain values only — no `styled-components` import, so the Server Component
 * layout can read this file. `css` fragments belong in ./css.ts.
 */

/**
 * Every colour the surface uses more than once. Single-use atmospheric
 * gradients (the aurora blooms, the glow washes) stay local to their
 * component, named — they are one-off artwork, not a scale.
 */
const colors = {
  /** Body copy on the dark canvas. */
  bodyLight: 'hsla(229, 60%, 88%, 1)',

  /** Hairline rules and the skip link's border, over the dark canvas. */
  hairline: 'hsla(229, 100%, 90%, 0.18)',

  /** Soft-border panels — bento cells, the demo stage. */
  panelFill: 'hsla(231, 30%, 9%, 0.72)',
  panelBorder: 'hsla(229, 100%, 90%, 0.12)',
  panelBorderStrong: 'hsla(229, 100%, 90%, 0.25)',
  /**
   * Solid card fill over the blue800 band — TripleUp's cards and WistaiTour's
   * active stop. Stays hex on purpose: no integer hsl() renders #0d1747
   * exactly (the nearest is #0C1746), and shifting it to satisfy the units
   * convention would be a real colour change.
   */
  cardFill: '#0d1747',

  /** The accent ramp's middle stop; the outer two are DVD blue300/pink400. */
  accentPurple: 'hsla(285, 100%, 78%, 1)',
  /** A faint blue300 wash behind accented elements. */
  accentWash: 'hsla(229, 100%, 80%, 0.15)',

  /** The cursor-trailing orb behind the closing CTA and footer. */
  orbViolet: 'hsla(265, 100%, 62%, 0.42)',
  orbBlue: 'hsla(230, 100%, 58%, 0.22)',

  /** Drop shadows. */
  shadowInk: 'hsla(232, 40%, 2%, 0.6)',
};

/** Only the steps DVD does not carry; everything else is `theme.typography`. */
const typography = {
  fontSize: {
    /** Every section headline: Aurora, Glow, TripleUp, WistaiTour,
     *  WatchAndLearn, closing CTA. */
    sectionTitle: '2rem', // 32px
    /** Hero headline and the proof stat numbers. */
    display: '2.25rem', // 36px
    desktop: {
      /** All four bands' eyebrows — a step below DVD's 1.25rem, applied by
       *  the `Eyebrow` primitive in ./styled.ts. */
      eyebrow: '1.125rem', // 18px
      sectionTitle: '3rem', // 48px
      display: '4rem', // 64px
    },
  },
};

/**
 * The motion steps DVD does not carry. Its own `transition.duration` (0.3s) is
 * still the right reach for a plain cross-fade, so there is no step for it
 * here.
 */
const transition = {
  duration: {
    /** Hover and focus feedback on chrome. */
    fast: '200ms',
    /** Positional moves far enough to need watching — stage swap, orb fade. */
    slow: '600ms',
    /** The hero's word-by-word rise, and the delay that staggers it. */
    entrance: '700ms',
  },
  easing: {
    /** Decelerating — entrances and stage swaps. */
    out: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
    /** Longer-tailed — the carousel track and the showcase card tilt. */
    glide: 'cubic-bezier(0.22, 1, 0.36, 1)',
  },
};

/** Reading and layout columns shared by two or more bands. */
const maxWidth = {
  prose: '44rem', // SectionLede, WistaiTour copy, hero sub
  column: '60rem', // SectionTitle, TripleUp, ProofBand headline
  band: '68rem', // BandTitle, hero headline
  wide: '76rem', // VideoStage, ProofBand stat row
  tour: '82rem', // WistaiTour rail and stage
};

export const darkHomeTokens = { colors, typography, transition, maxWidth };
